import { environment } from '@awork/environments/environment'

const PORTLESS_APP_LABEL_PREFIX = 'awork-'
const SHARED_LABEL_SEPARATOR = '--'

export interface PortlessHostname {
  /** Hostname used for cookies and API hostname detection. */
  readonly baseHostname: string
  readonly subdomain: string | null
  /** Public share name for <workspace>--<name>.<domain> hosts, else null. */
  readonly shareName: string | null
}

/**
 * Parses a public start-awork share hostname: <workspace>--<name>.<domain>.
 * Only local builds use this shape, so other environments never match.
 * @param {string[]} labels
 * @returns {PortlessHostname | null}
 */
function parseSharedHostname(labels: string[]): PortlessHostname | null {
  if (environment !== 'local' || labels.length < 3) {
    return null
  }

  const [firstLabel, ...domainLabels] = labels
  const separatorIndex = firstLabel.indexOf(SHARED_LABEL_SEPARATOR)

  if (separatorIndex <= 0) {
    return null
  }

  const subdomain = firstLabel.slice(0, separatorIndex)
  const shareName = firstLabel.slice(separatorIndex + SHARED_LABEL_SEPARATOR.length)

  if (!shareName || shareName.includes(SHARED_LABEL_SEPARATOR)) {
    return null
  }

  return { baseHostname: domainLabels.join('.'), subdomain, shareName }
}

/**
 * Parses an awork Portless hostname.
 * Supports <workspace>.awork-<name>.localhost and <workspace>--<name>.<domain>.
 * @param {string} hostname
 * @returns {PortlessHostname | null}
 */
export function parsePortlessHostname(hostname: string): PortlessHostname | null {
  const labels = hostname.toLowerCase().split('.')
  const appLabelIndex = labels.length - 2

  if (labels.at(-1) !== 'localhost') {
    return parseSharedHostname(labels)
  }

  if (labels.length < 2 || labels.length > 3 || !labels[appLabelIndex].startsWith(PORTLESS_APP_LABEL_PREFIX)) {
    return null
  }

  return {
    baseHostname: labels.slice(appLabelIndex).join('.'),
    subdomain: appLabelIndex === 1 ? labels[0] : null,
    shareName: null
  }
}

/**
 * Builds the hostname of a workspace on the same Portless or shared app.
 * @param {PortlessHostname} portlessHostname
 * @param {string} subdomain
 * @returns {string}
 */
export function getPortlessWorkspaceHostname(portlessHostname: PortlessHostname, subdomain: string): string {
  if (portlessHostname.shareName) {
    return `${subdomain}${SHARED_LABEL_SEPARATOR}${portlessHostname.shareName}.${portlessHostname.baseHostname}`
  }

  return `${subdomain}.${portlessHostname.baseHostname}`
}

/**
 * Builds a Portless origin and keeps the browser-visible tunnel port.
 * @param {string} subdomain
 * @param {Location} [browserLocation=window.location]
 * @returns {string | null}
 */
export function getPortlessWorkspaceOrigin(
  subdomain: string,
  browserLocation: Location = window.location
): string | null {
  const portlessHostname = parsePortlessHostname(browserLocation.hostname)

  if (!portlessHostname) {
    return null
  }

  const port = browserLocation.port ? `:${browserLocation.port}` : ''

  return `${browserLocation.protocol}//${getPortlessWorkspaceHostname(portlessHostname, subdomain)}${port}`
}
